import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { TablePagination } from '../../../components/TablePagination';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import { RosterUploadsFilters } from './RosterUploadsFilters';
import { RosterUploadsSummary } from './RosterUploadsSummary';
import { RosterUploadsTable } from './RosterUploadsTable';
import {
  DEFAULT_FILTERS,
  ROSTER_UPLOADS_PAGE_SIZE,
  buildRosterUploadsParams,
  hasActiveFilters,
  parseRosterUploadsParams,
  rosterUploadsHref,
  type RosterUploadsFilters as Filters,
} from './rosterUploadsNavigation';
import { useRosterUploadOrganizations, useRosterUploads } from './useRosterUploads';
import '../../../components/DataTable.css';
import './RosterUploads.css';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Uploaded Rosters: the latest roster uploads across every organization, newest first, with a
 * summary over everything the filters match.
 *
 * **The URL is the only copy of the filters.** They are read from it on every render rather than
 * mirrored into state, so a per-organization link (`?org=<id>`), a row's organization link and the
 * browser's back button all land on exactly what they name. Only the search box keeps a local
 * draft, so typing is not a navigation per keystroke; it reaches the URL once typing pauses.
 */
export const RosterUploadsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseRosterUploadsParams(searchParams), [searchParams]);

  const { data, receivedAt, loading, error, reload } = useRosterUploads(filters);
  const { organizations, failed: organizationsFailed } = useRosterUploadOrganizations();

  const updateFilters = useCallback(
    (changes: Partial<Filters>) => {
      setSearchParams(
        (current) => buildRosterUploadsParams({ ...parseRosterUploadsParams(current), ...changes }),
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /*
    The latest `updateFilters`, for the search timer. react-router's functional `setSearchParams`
    merges into the `searchParams` of the render that created it, not the live URL, so a timer that
    kept the updater from the keystroke's render would write that render's filters back — undoing a
    status or source picked during the 300 ms wait.
  */
  const updateFiltersRef = useRef(updateFilters);
  /** The URL's filters as of the last commit, for the same timer: is its search already there? */
  const filtersRef = useRef(filters);
  useLayoutEffect(() => {
    updateFiltersRef.current = updateFilters;
    filtersRef.current = filters;
  }, [updateFilters, filters]);

  /* ---------------------------------------------------------------- search draft */

  const [searchInput, setSearchInput] = useState(filters.search);
  const [searchInUrl, setSearchInUrl] = useState(filters.search);
  /**
   * The search the box last wrote to the URL, until the URL's search next changes. Cleared on that
   * change whatever caused it, and never set for a push the URL already matches (which changes
   * nothing, so nothing would ever clear it) — a leftover marker would later mistake an unrelated
   * change back to the same text, such as browser back, for the box's own push.
   */
  const [pushedSearch, setPushedSearch] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
    The URL's search changed. Adjusted during render rather than in an effect, so the box never
    shows one frame of the old text.

    Only a change the box did NOT cause replaces the draft — browser back, a pasted link, Clear
    filters. The box's own change is recognised by the value it pushed, not by comparing with the
    live draft: the draft may already hold keystrokes typed after the push, and comparing against
    it would overwrite them with the older pushed text.
  */
  if (filters.search !== searchInUrl) {
    setSearchInUrl(filters.search);
    setPushedSearch(null);
    if (filters.search !== pushedSearch) setSearchInput(filters.search);
  }

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchTimer.current = null;
      const search = value.trim();
      // Typed away and back again: the URL already says this, so there is nothing to push — and
      // pushing would also throw away the page the admin was on.
      if (search === filtersRef.current.search) return;
      setPushedSearch(search);
      updateFiltersRef.current({ search, page: 1 });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClear = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = null;
    setSearchInput('');
    setPushedSearch(null);
    setSearchParams(buildRosterUploadsParams(DEFAULT_FILTERS), { replace: true });
  };

  /* ---------------------------------------------------------------- paging */

  /*
    A page past the end — a stale link, or the last upload on the last page falling out of a
    filter — would otherwise show an empty table that claims there is nothing to see.
  */
  const lastPage = data?.totalPages ?? 0;
  useEffect(() => {
    // Not after a failure: `data` is then an older response, whose page count may belong to
    // other filters.
    if (!loading && !error && lastPage > 0 && filters.page > lastPage) {
      updateFilters({ page: lastPage });
    }
  }, [loading, error, lastPage, filters.page, updateFilters]);

  /* ---------------------------------------------------------------- retry focus */

  /*
    The error banner stays up while a retry is in flight (the hook keeps `error` until the next
    request settles), so the Try again button that was just pressed keeps focus. When the retry
    succeeds the banner goes, taking the button with it, and focus would fall to the body; it is
    moved to the result count instead, which is also what just changed. Only when nothing else has
    taken focus in the meantime, so an admin who moved on is not pulled back.
  */
  const resultCountRef = useRef<HTMLParagraphElement>(null);
  const retryPendingRef = useRef(false);
  const handleRetry = () => {
    retryPendingRef.current = true;
    reload();
  };
  useLayoutEffect(() => {
    if (loading || !retryPendingRef.current) return;
    retryPendingRef.current = false;
    if (error) return;
    const active = document.activeElement;
    if (!active || active === document.body) resultCountRef.current?.focus();
  }, [loading, error]);

  /* ---------------------------------------------------------------- derived */

  const selectedOrganizationName = useMemo(() => {
    if (!filters.organizationId) return null;
    const listed = organizations.find((org) => org.id === filters.organizationId);
    if (listed) return listed.name;
    const fromRow = data?.items.find((item) => item.organizationId === filters.organizationId);
    return fromRow?.organizationName ?? null;
  }, [filters.organizationId, organizations, data]);

  const backTo = rosterUploadsHref(filters);
  const filtered = hasActiveFilters(filters);

  return (
    <AdminPage width="full" className="roster-uploads" labelledBy="roster-uploads-title">
      <AdminPageHeader
        title="Uploaded Rosters"
        titleId="roster-uploads-title"
        subtitle="Every roster an organization has uploaded, newest first. Open one to see its players, files and extraction metrics."
        actions={
          filters.organizationId && (
            <p className="roster-uploads__scope" data-testid="roster-uploads-scope">
              Showing uploads for{' '}
              <strong>{selectedOrganizationName ?? 'one organization'}</strong>
              {' · '}
              <Link to={`/admin/organizations/${filters.organizationId}`}>Organization details</Link>
            </p>
          )
        }
      />

      {data && (
        <RosterUploadsSummary
          summary={data.summary}
          now={receivedAt}
          freshness={loading ? 'loading' : error ? 'outdated' : 'current'}
        />
      )}

      <RosterUploadsFilters
        filters={filters}
        searchInput={searchInput}
        organizations={organizations}
        organizationsFailed={organizationsFailed}
        selectedOrganizationFallback={selectedOrganizationName}
        onSearchInputChange={handleSearchInputChange}
        onFiltersChange={updateFilters}
        onClear={handleClear}
      />

      {error && (
        <div className="admin-error" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={handleRetry}
            data-testid="roster-uploads-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {/*
        Always mounted, so the result count is announced when it changes. Focusable only from script,
        for the retry above.
      */}
      <p
        role="status"
        className="roster-uploads__result-count"
        ref={resultCountRef}
        tabIndex={-1}
        data-testid="roster-uploads-result-count"
      >
        {loading
          ? 'Loading uploaded rosters...'
          : data
            ? `${data.totalCount.toLocaleString('en-US')} ${data.totalCount === 1 ? 'upload' : 'uploads'}`
            : ''}
      </p>

      {/*
        Stays mounted while the next page or filter loads, and after one fails. Unmounting it on
        every request removed the pagination button that had just been pressed, dropping keyboard
        focus to the body; a failure did the same, and left nothing on screen but the error. The
        rows shown after a failure are the last successful load, which the summary above says.
      */}
      {data && (
        <div
          className={`roster-uploads__results${loading ? ' roster-uploads__results--stale' : ''}`}
          aria-busy={loading}
          data-testid="roster-uploads-results"
        >
          <RosterUploadsTable
            items={data.items}
            now={receivedAt}
            backTo={backTo}
            emptyMessage={
              filtered ? 'No uploads match these filters.' : 'No rosters have been uploaded yet.'
            }
          />
          <TablePagination
            currentPage={filters.page}
            pageSize={ROSTER_UPLOADS_PAGE_SIZE}
            totalCount={data.totalCount}
            onPageChange={(page) => updateFilters({ page })}
          />
        </div>
      )}
    </AdminPage>
  );
};
